import * as React from "react";
import {
  createNestablePublicClientApplication,
  InteractionRequiredAuthError,
  type IPublicClientApplication,
  type AccountInfo,
  AuthenticationResult,
} from "@azure/msal-browser";

import { AppRole } from "../api/types";
import { msalConfig, apiScopes } from "./authConfig";
import { MockAuthProvider } from "./MockAuthProvider";

const USE_MOCK_AUTH = process.env.MOCK_AUTH !== "false";

export interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  userRoles: AppRole[];
  accessToken: string | null;
  userName: string | null;
  getAccessToken: () => Promise<string>;
}

export const AuthContext = React.createContext<AuthContextType>({
  isAuthenticated: false,
  isLoading: true,
  error: null,
  userRoles: [],
  accessToken: null,
  userName: null,
  getAccessToken: () => Promise.reject(new Error("AuthProvider not initialized")),
});

export const useAuth = () => React.useContext(AuthContext);

/** Extract AppRole values from the ID token roles claim. */
function extractRoles(claims: Record<string, unknown> | undefined): AppRole[] {
  const roles = claims?.roles;
  if (!Array.isArray(roles)) {
    return [];
  }

  const validRoles = new Set<string>(Object.values(AppRole));
  return roles.filter((r): r is AppRole => validRoles.has(r));
}

/**
 * Real MSAL auth provider using Nested App Authentication (NAA).
 * Acquires tokens silently via SSO, falling back to popup for consent/MFA.
 */
const MsalAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = React.useState<Omit<AuthContextType, "getAccessToken">>({
    isAuthenticated: false,
    isLoading: true,
    error: null,
    userRoles: [],
    accessToken: null,
    userName: null,
  });

  const msalInstanceRef = React.useRef<IPublicClientApplication | null>(null);
  const accountRef = React.useRef<AccountInfo | null>(null);

  React.useEffect(() => {
    let cancelled = false;

    async function initialize() {
      // Check NAA support
      if (!Office.context.requirements.isSetSupported("NestedAppAuth", "1.1")) {
        if (!cancelled) {
          setState((s) => ({
            ...s,
            isLoading: false,
            error: "This version of Office does not support single sign-on. Please update Office.",
          }));
        }
        return;
      }

      try {
        // Initialize MSAL
        const msalInstance = await createNestablePublicClientApplication(msalConfig);
        msalInstanceRef.current = msalInstance;

        // Acquire token
        const tokenRequest = { scopes: apiScopes };

        let result: AuthenticationResult;

        try {
          result = await msalInstance.acquireTokenSilent(tokenRequest);
        }
        catch (silentError) {
          if (silentError instanceof InteractionRequiredAuthError) {
            console.info("Silent token acquisition failed, acquiring via popup...", silentError);

            result = await msalInstance.acquireTokenPopup(tokenRequest);
          }
          else {
            throw silentError;
          }
        }

        accountRef.current = result.account;
        const roles = extractRoles(result.idTokenClaims as Record<string, unknown>);

        if (!cancelled) {
          setState({
            isAuthenticated: true,
            isLoading: false,
            error: null,
            userRoles: roles,
            accessToken: result.accessToken,
            userName: result.account?.name ?? null,
          });
        }
      } 
      catch (err) {
        if (!cancelled) {
          setState((s) => ({
            ...s,
            isLoading: false,
            error: err instanceof Error ? err.message : "Authentication failed. Please try again.",
          }));
        }
      }
    }

    initialize();

    return () => {
      cancelled = true;
    };
  }, []);

  const getAccessToken = React.useCallback(async (): Promise<string> => {
    const msalInstance = msalInstanceRef.current;
    if (!msalInstance) {
      throw new Error("MSAL is not initialized");
    }

    const tokenRequest = {
      scopes: apiScopes,
      account: accountRef.current ?? undefined,
    };

    try {
      const result = await msalInstance.acquireTokenSilent(tokenRequest);
      return result.accessToken;
    } catch (silentError) {
      if (silentError instanceof InteractionRequiredAuthError) {
        const result = await msalInstance.acquireTokenPopup(tokenRequest);
        return result.accessToken;
      }
      throw silentError;
    }
  }, []);

  const contextValue = React.useMemo<AuthContextType>(
    () => ({ ...state, getAccessToken }),
    [state, getAccessToken]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

/**
 * Auth provider that delegates to MockAuthProvider or MsalAuthProvider
 * based on the MOCK_AUTH environment variable.
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  if (USE_MOCK_AUTH) {
    return <MockAuthProvider>{children}</MockAuthProvider>;
  }

  return <MsalAuthProvider>{children}</MsalAuthProvider>;
};

export default AuthProvider;
